/**
 * PolicyRegistrationCell - the Brokermandat registration of one policy on the customer's Policen list
 * (P5-S5, Kap. 7.12/7.13): the derived status in Stefan's words as a status chip, and the per-policy
 * exception: «Ausnehmen» on a linked policy for a caller with policy.write, «Ausnahme aufheben» on an
 * excepted one for a caller with policy.write AND mandate.eingangskontrolle, behind a small inline form
 * with the required, audited reason and the required Beleg (p5s5-r2-call3): a mail or a stored
 * attachment of the policy's registration (GET /registrations/:regId/beleg-options) or one of the
 * customer's documents. No chip for a policy that is not registered. A policy that inherited its
 * registration (P5-S7, Vererbung) names the policy it inherited from in the chip's tooltip.
 *
 * The api derives the status and is the hard line on the permission; the cell only renders it.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { REGISTRATION_EXCEPTION_LIFT_REASON_LABEL, REGISTRATION_EXCEPTION_LIFT_REASON_VALUES } from '@brokly/shared';
import type { PolicyRegistrationAusnahmeBody, PolicyRegistrationStatus, RegistrationExceptionLiftReason } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { StatusDot, type StatusTone } from '../../../components/ui/StatusDot';
import { apiClient } from '../../../lib/api-client';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { registrationErrorText } from '../../mandate/registration-error-text';
import { pickedBeleg, useRegistrationBelege } from '../../mandate/registration-beleg';
import { RegistrationBelegSelect } from '../../mandate/RegistrationBelegSelect';
import type { Policy } from '../types';

/** The key of POST /policies/:id/registration-ausnahme (setting an exception). */
export const POLICY_EXCEPTION_KEY = 'policy.write';
/** Lifting an exception needs both keys (the api answers 403 otherwise). */
export const POLICY_EXCEPTION_LIFT_KEYS = ['policy.write', 'mandate.eingangskontrolle'] as const;

const TONE: Record<PolicyRegistrationStatus, StatusTone> = {
  nicht_registriert: 'neutral',
  gemeldet: 'info',
  erinnert: 'warning',
  klaerung: 'warning',
  rueckfrage: 'warning',
  eingetragen: 'success',
  eingetragen_vererbt: 'success',
  ausgenommen: 'neutral',
  nicht_courtageberechtigt: 'danger',
  geschlossen: 'neutral',
  historisch: 'neutral',
  ausgetragen: 'neutral',
};

/**
 * The inline form of «Ausnahme aufheben»: reason and Beleg, both required. Mounted only while open, so
 * the Policen list reads no Beleg options per row.
 */
function LiftForm({
  policy,
  pending,
  onSubmit,
  onCancel,
}: {
  policy: Policy;
  pending: boolean;
  onSubmit: (body: PolicyRegistrationAusnahmeBody) => void;
  onCancel: () => void;
}) {
  const [liftReason, setLiftReason] = useState<RegistrationExceptionLiftReason | ''>('');
  const [belegId, setBelegId] = useState('');
  const belege = useRegistrationBelege(policy.registrationId, policy.kundeId);
  const beleg = pickedBeleg(belege, belegId);
  const hasBeleg = beleg.documentId !== undefined || beleg.communicationId !== undefined;
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="policy-registration-lift-form">
      <select
        className="rounded border px-1 py-0.5 text-xs"
        aria-label="Grund der Aufhebung"
        value={liftReason}
        onChange={(e) => setLiftReason(e.target.value as RegistrationExceptionLiftReason | '')}
      >
        <option value="">Grund wählen</option>
        {REGISTRATION_EXCEPTION_LIFT_REASON_VALUES.map((v) => (
          <option key={v} value={v}>
            {REGISTRATION_EXCEPTION_LIFT_REASON_LABEL[v]}
          </option>
        ))}
      </select>
      <RegistrationBelegSelect
        belege={belege}
        value={belegId}
        onChange={setBelegId}
        placeholder="Beleg wählen"
        className="rounded border px-1 py-0.5 text-xs"
      />
      <Button
        size="sm"
        disabled={pending || liftReason === '' || !hasBeleg}
        onClick={() => liftReason !== '' && hasBeleg && onSubmit({ lift: true, liftReasonCode: liftReason, ...beleg })}
        data-testid="policy-registration-lift-confirm"
      >
        Aufheben
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Abbrechen
      </Button>
    </span>
  );
}

/** `readOnly`: the customer is inaktiv (write-protected): the chip only. */
export function PolicyRegistrationCell({ policy, readOnly = false }: { policy: Policy; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const canWrite = can(POLICY_EXCEPTION_KEY) && !readOnly;
  const canLift = POLICY_EXCEPTION_LIFT_KEYS.every((k) => can(k)) && !readOnly;
  const [lifting, setLifting] = useState(false);
  const mutation = useMutation({
    mutationFn: (body: PolicyRegistrationAusnahmeBody) => apiClient.policies.registrationAusnahme(policy.id, body),
    onSuccess: () => {
      setLifting(false);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['policies'] });
      // Every registration read (list, subject view, tile) sits under this prefix.
      void qc.invalidateQueries({ queryKey: ['registrations'] });
    },
  });

  const status = policy.registrationStatus;
  if (status === undefined || status === 'nicht_registriert') return null;
  const excepted = policy.registrationExceptionReason != null;
  const linked = policy.registrationId != null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span
        data-testid="policy-registration-chip"
        title={policy.registrationInheritedFromPolicyNumber ? `Vererbt von Police ${policy.registrationInheritedFromPolicyNumber}` : undefined}
      >
        <StatusDot tone={TONE[status] ?? 'neutral'} label={policy.registrationStatusLabel ?? status} />
      </span>
      {canLift && excepted && !lifting && (
        <Button size="sm" variant="ghost" disabled={mutation.isPending} onClick={() => setLifting(true)} data-testid="policy-registration-lift">
          Ausnahme aufheben
        </Button>
      )}
      {canLift && excepted && lifting && (
        <LiftForm
          policy={policy}
          pending={mutation.isPending}
          onSubmit={(body) => mutation.mutate(body)}
          onCancel={() => {
            setLifting(false);
            mutation.reset();
          }}
        />
      )}
      {canWrite && !excepted && linked && (
        <Button
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ reason: 'broker' })}
          data-testid="policy-registration-exclude"
        >
          Ausnehmen
        </Button>
      )}
      {mutation.isError && (
        <span className="text-destructive" role="alert">
          {registrationErrorText(mutation.error)}
        </span>
      )}
    </div>
  );
}
